import {useSuspenseQuery} from '@tanstack/react-query'
import {Link} from '@tanstack/react-router'
import {BlazeConfig} from 'blaze-slider'
import {useTranslations} from 'use-intl'
import {memo, useMemo} from 'react'
import {
    getCompositionImageBaseUrl,
    getDesignImageSrc,
    getDesignImageSrcSet,
    getProductModelImageSrc,
    getProductModelImageSrcSet,
} from '~/utils/product'
import {collectionProductsPreviewQueryOptions} from '~/client/fn/collectionProductsPreview'
import {defaultImagesQueryOptions} from '~/client/fn/defaultImages'
import ImageComposition from '~/components/ImageComposition'
import useLocaleParams from '~/hooks/useLocaleParams'
import Carousel from '~/components/common/Carousel'
import useShopConfig from '~/hooks/useShopConfig'
import Price from '~/components/common/Price'
import Card from '~/components/common/Card'
import {ImageFormat} from '~/gql/graphql'

type ProductPreviewProps = {
    collectionId: string
}

const config: BlazeConfig = {
    all: {
        slidesToShow: 1.25,
        loop: false,
    },
    '(min-width: 520px)': {
        slidesToShow: 2.25,
        slidesToScroll: 2,
    },
    '(min-width: 768px)': {
        slidesToShow: 3.25,
        slidesToScroll: 3,
    },
    '(min-width: 1024px)': {
        slidesToShow: 4.25,
        slidesToScroll: 4,
    },
}

export const ProductPreview = ({collectionId}: ProductPreviewProps) => {
    const params = useLocaleParams()
    const t = useTranslations('collectionDetails.relatedProducts')
    const shopConfig = useShopConfig()
    const {data: defaultImages} = useSuspenseQuery(defaultImagesQueryOptions(params.locale))
    const {data: products} = useSuspenseQuery(
        collectionProductsPreviewQueryOptions(collectionId, params.locale)
    )

    const productsWithImage = useMemo(() => {
        return products.map((product) => {
            const productTypeImage = defaultImages.find(
                (image) => image.productTypeId === product.type?.id
            )?.images

            const baseProductImageSrc = `${shopConfig.imageServerEndpoint}/products/${product.vpId}`
            const baseDesignImageSrc = getCompositionImageBaseUrl(baseProductImageSrc)!

            return {
                name: product.name,
                price: product.price,
                productId: product.id,
                appearanceId: product.defaultAppearanceId,
                image: {
                    src: getProductModelImageSrc(
                        baseProductImageSrc,
                        product.defaultViewId ?? '1',
                        product.defaultAppearanceId ?? '2',
                        productTypeImage?.modelId,
                        productTypeImage?.crop
                    ),
                    srcset: getProductModelImageSrcSet(
                        baseProductImageSrc,
                        product.defaultViewId ?? '1',
                        product.defaultAppearanceId ?? '2',
                        productTypeImage?.modelId,
                        productTypeImage?.crop
                    ),
                    altText: product.name,
                    format: ImageFormat.Png,
                },
                designImage: {
                    src: getDesignImageSrc(
                        baseDesignImageSrc,
                        product.defaultViewId ?? '1',
                        product.defaultAppearanceId ?? '2'
                    ),
                    srcset: getDesignImageSrcSet(
                        baseDesignImageSrc,
                        product.defaultViewId ?? '1',
                        product.defaultAppearanceId ?? '2'
                    ),
                    altText: product.image?.altText,
                },
            }
        })
    }, [defaultImages, products, shopConfig.imageServerEndpoint])

    return (
        <div className="relative">
            <Carousel config={config} showDots={false} showDisabledArrows={true}>
                {productsWithImage.map((product) => (
                    <Link
                        className="block h-full group outline-none"
                        key={product.productId}
                        to="/$lang/products/$productId"
                        params={{...params, productId: product.productId!}}
                        search={{appearanceId: product.appearanceId!}}
                        aria-label={t('goToProductDetails', {product: product.name})}
                        title={product.name!}
                    >
                        <Card className="relative h-full flex flex-col group-hover:bg-gray-700/60 group-focus-visible:bg-gray-700/60 group-focus-visible:ring-2 ring-purple-600 transition duration-200 ease-in-out transform-gpu">
                            <ImageComposition
                                className="overflow-hidden"
                                imageClassName="p-4 rounded scale-100 opacity-100 group-hover:scale-150 group-hover:pointer-events-none group-hover:opacity-40 group-focus-visible:scale-150 group-focus-visible:opacity-40 motion-safe:transition motion-safe:duration-200 motion-safe:ease-in-out"
                                image={product.image}
                                sizes="500px"
                            />
                            <div className="absolute opacity-0 scale-75 group-hover:opacity-100 group-hover:scale-100 group-focus-visible:scale-100 group-focus-visible:opacity-100 inset-0 p-4 motion-safe:transition motion-safe:duration-200 motion-safe:ease-in-out">
                                <ImageComposition
                                    className="rounded-md overflow-hidden bg-white"
                                    image={product?.designImage!}
                                    lazy={true}
                                    sizes="500px"
                                />
                            </div>
                            <div className="flex gap-4 flex-row justify-between p-4 text-sm font-medium">
                                <div className="truncate">{product.name}</div>
                                <Price className="text-nowrap">{product.price!.vatIncluded}</Price>
                            </div>
                        </Card>
                    </Link>
                ))}
                {productsWithImage.length > 0 && (
                    <Link
                        aria-label={t('showAll')}
                        to="/$lang/products"
                        params={params}
                        search={{collection: `T${collectionId}`}}
                    >
                        <Card>
                            <div className="aspect-ratio p-6">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 256 256"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="4"
                                    strokeDasharray="6 12"
                                >
                                    <path d="M192,120h28.34a8.44,8.44,0,0,0,7.5-4.42l19.27-36.81a7.81,7.81,0,0,0-3.33-10.52L192,40" />
                                    <path d="M64,120H35.66a8.44,8.44,0,0,1-7.5-4.42L8.89,78.77a7.81,7.81,0,0,1,3.33-10.52L64,40" />
                                    <path d="M160,40a32,32,0,0,1-64,0H64V208a8,8,0,0,0,8,8H184a8,8,0,0,0,8-8V40Z" />
                                </svg>
                            </div>
                            <div className="flex gap-4 flex-row justify-center p-4 text-sm font-medium">
                                {t('showAll')}
                            </div>
                        </Card>
                    </Link>
                )}
            </Carousel>
        </div>
    )
}

export const ProductsPreviewSkeletonLoader = memo(() => {
    const t = useTranslations('collectionDetails.relatedProducts')

    return (
        <div>
            <Carousel
                config={{...config, all: {...config.all, draggable: false}}}
                showDots={false}
                showArrows={false}
            >
                {[...new Array(8)].map((_, index) => (
                    <Card key={index} className="p-4" aria-label={t('loading')}>
                        <div className="p-4 aspect-square bg-gray-900/30 animate-pulse rounded w-full" />
                        <div className="m-4 bg-gray-900/30 animate-pulse rounded h-5 w-1/2" />
                    </Card>
                ))}
            </Carousel>
        </div>
    )
})
